import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const CTypeCasting = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Operators</div>
                <h1>Type Casting & Conversions</h1>
                <p className="hero-lead">
                    Master type conversions in C - both implicit (automatic) and explicit (manual casting). Learn to
                    safely convert between data types, understand type promotion, and avoid data loss and undefined behavior.
                </p>
            </div>

            <div className="content-section">
                <h2>Understanding Type Conversion</h2>
                <p>
                    Type conversion is the process of converting a value from one data type to another. C performs some
                    conversions automatically (implicit conversion), while others require explicit instruction (explicit
                    conversion or casting). Understanding when and how conversions happen prevents bugs caused by unexpected
                    data loss, overflow, or precision errors.
                </p>
                <p>
                    C is a statically typed language - every variable has a fixed type. However, operations often involve
                    multiple types (mixing int and double, for example). The compiler must convert values to compatible
                    types before performing operations. These conversion rules are complex but follow consistent patterns
                    based on type hierarchy and safety considerations.
                </p>

                <div className="code-example">
                    <CodeBlock code={`#include <stdio.h>

int main(void) {
    /* Implicit conversion (automatic) */
    int i = 10;
    double d = i;  // int automatically converted to double
    printf("d = %f\\n", d);  // 10.000000
    
    /* Explicit conversion (manual cast) */
    double x = 3.14;
    int y = (int)x;  // Cast double to int (truncates)
    printf("y = %d\\n", y);  // 3
    
    /* Mixed arithmetic triggers conversion */
    int a = 5;
    double b = 2.0;
    double result = a + b;  // a converted to double first
    printf("result = %f\\n", result);  // 7.000000
    
    return 0;
}`} language="c" />
                </div>
            </div>

            <div className="content-section">
                <h2>Implicit Type Conversion</h2>
                <p>
                    Implicit conversion (also called automatic or coercion) happens without explicit instruction from the
                    programmer. The compiler automatically converts values when needed, following promotion rules. These
                    conversions generally move from "narrower" types to "wider" types to preserve data, though narrowing
                    conversions can happen in assignments.
                </p>

                <h3>Assignment Conversions</h3>
                <p>
                    When you assign a value to a variable of a different type, C converts the value to match the variable's
                    type. Widening conversions (small type to larger type) are safe. Narrowing conversions (large type to
                    smaller type) can lose data - the compiler may warn about these.
                </p>

                <div className="code-example">
                    <CodeBlock code={`/* Widening conversions (safe) */
char c = 'A';
int i = c;  // char promoted to int (no data loss)
printf("char 'A' as int: %d\\n", i);  // 65

int num = 100;
long l = num;  // int promoted to long (no data loss)

float f = 3.14f;
double d = f;  // float promoted to double (more precision)

/* Narrowing conversions (may lose data) */
int big = 300;
char small = big;  // int truncated to char
printf("300 as char: %d\\n", small);  // 44 (300 % 256)

double precise = 3.14159;
int truncated = precise;  // Fractional part lost
printf("3.14159 as int: %d\\n", truncated);  // 3

long long huge = 9999999999LL;
int overflow = huge;  // Data loss/overflow
printf("Overflow: %d\\n", overflow);  // Undefined!

/* Signed/unsigned conversions */
int signed_val = -5;
unsigned int unsigned_val = signed_val;  // Wraps around
printf("-5 as unsigned: %u\\n", unsigned_val);  // Very large number

unsigned int big_unsigned = 4000000000U;
int signed_again = big_unsigned;  // May overflow
printf("Overflow: %d\\n", signed_again);  // Negative or undefined`} language="c" />
                </div>

                <h3>Arithmetic Conversions</h3>
                <p>
                    When an operation involves two different types, C converts both operands to a common type using the
                    "usual arithmetic conversions." Generally, both operands are promoted to the "higher" type in the
                    hierarchy: long double {">"} double {">"} float {">"} unsigned long {">"} long {">"} unsigned int {">"} int.
                </p>

                <div className="code-example">
                    <CodeBlock code={`/* Integer promotion in expressions */
char a = 10, b = 20;
int result = a + b;  // Both promoted to int, then added
printf("%d + %d = %d\\n", a, b, result);  // 10 + 20 = 30

/* Mixed int and double */
int x = 5;
double y = 2.5;
double z = x + y;  // x promoted to double, then added
printf("%d + %f = %f\\n", x, y, z);  // 5 + 2.5 = 7.5

/* Unexpected integer division */
int num = 5, denom = 2;
double quotient = num / denom;  // Division done as int first!
printf("Wrong: %d / %d = %f\\n", num, denom, quotient);  // 2.000000

/* Force double arithmetic */
double correct = (double)num / denom;  // num converted, then division
printf("Correct: %d / %d = %f\\n", num, denom, correct);  // 2.500000

/* Float and double mixed */
float f = 3.14f;
double d = 2.5;
double result2 = f * d;  // f promoted to double
printf("%f * %f = %f\\n", f, d, result2);

/* Integer types of different sizes */
short s = 100;
long l = 200;
long result3 = s + l;  // s promoted to long
printf("%d + %ld = %ld\\n", s, l, result3);`} language="c" />
                </div>
            </div>

            <div className="content-section">
                <h2>Explicit Type Casting</h2>
                <p>
                    Explicit casting uses the cast operator to manually convert a value to another type. Syntax: (type)value.
                    Casting overrides implicit conversion rules, giving you precise control. Use explicit casts to clarify
                    intent, avoid warnings, or perform conversions that wouldn't happen automatically.
                </p>

                <div className="code-example">
                    <CodeBlock code={`/* Basic casting syntax */
double d = 3.14159;
int i = (int)d;  // Explicit cast: double to int
printf("%f cast to int: %d\\n", d, i);  // 3

float f = (float)d;  // double to float
printf("%f as float: %f\\n", d, f);  // 3.141590

/* Cast to force floating-point division */
int a = 7, b = 3;
double result = (double)a / b;  // Cast one operand
printf("%d / %d = %f\\n", a, b, result);  // 7 / 3 = 2.333333

/* Alternative: Cast either operand */
result = a / (double)b;  // Also works
result = (double)a / (double)b;  // Both cast (redundant)

/* Cast for clarity */
double average = (double)(sum) / count;  // Clear intention

/* Cast in function calls */
int ceil_result = (int)(sqrt(25.0));  // sqrt returns double

/* Cast with sizeof */
size_t size = sizeof(int);
int size_int = (int)size;  // Cast size_t to int

/* Pointer casts (advanced) */
int x = 42;
void *vptr = &x;  // Implicit cast to void*
int *iptr = (int*)vptr;  // Explicit cast back to int*
printf("Value: %d\\n", *iptr);  // 42

/* Casting in printf */
long long big = 1234567890LL;
printf("%d\\n", (int)big);  // Cast to int for %d format`} language="c" />
                </div>
            </div>

            <div className="content-section">
                <h2>Type Promotion Rules</h2>
                <p>
                    Integer promotion automatically converts small integer types (char, short) to int before arithmetic
                    operations. This is called "integer promotion." The rule exists because most CPUs operate on int-sized
                    values most efficiently. Understanding promotion prevents surprises in expressions involving small types.
                </p>

                <div className="code-example">
                    <CodeBlock code={`/* Integer promotion */
char a = 100, b = 50;
/* Even though both are char, addition uses int */
int result = a + b;  // a and b promoted to int
printf("char %d + char %d = int %d\\n", a, b, result);

/* Promotion in comparisons */
char c1 = -1;  // 11111111 in binary (signed char)
unsigned char c2 = 255;  // 11111111 in binary (unsigned)

/* Both promoted to int for comparison */
if (c1 == c2) {  // False! -1 != 255 as int
    printf("Equal\\n");
} else {
    printf("Not equal\\n");
}

/* Promotion with bitwise operators */
unsigned char x = 0xFF;  // 11111111
unsigned char y = ~x;  // Promoted to int, then inverted!

/* Result is int, not char */
printf("Type of ~x: ");
if (sizeof(~x) == sizeof(int)) {
    printf("int\\n");
}

/* Usual arithmetic conversions hierarchy */
// long double (highest)
// double
// float
// unsigned long long
// long long
// unsigned long
// long
// unsigned int
// int (lowest of "promoted" types)

/* Example of hierarchy */
unsigned int ui = 10;
long l = 20;
// Both converted to unsigned long (if long can hold all unsigned int values)
// Or to long long, depending on system`} language="c" />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Pitfalls</h2>
                <p>
                    Type conversions are a frequent source of bugs. Understanding these common pitfalls helps you write
                    safer, more predictable code. Always be mindful of potential data loss, sign changes, and precision
                    issues when converting between types.
                </p>

                <div className="code-example">
                    <CodeBlock code={`/* Pitfall 1: Integer division before cast */
int a = 5, b = 2;
double wrong = (double)(a / b);  // Casts result of int division (2)
printf("Wrong: %f\\n", wrong);  // 2.000000

double correct = (double)a / b;  // Casts before division
printf("Correct: %f\\n", correct);  // 2.500000

/* Pitfall 2: Overflow in intermediate calculations */
int x = 100000;
int y = 100000;
long long product = x * y;  // Overflow before assignment!
printf("Wrong product: %lld\\n", product);  // Overflow

long long correct_product = (long long)x * y;  // Cast before multiply
printf("Correct product: %lld\\n", correct_product);

/* Pitfall 3: Comparing signed and unsigned */
int signed_val = -1;
unsigned int unsigned_val = 1;

if (signed_val < unsigned_val) {  // signed converted to unsigned!
    printf("Comparison works\\n");
} else {
    printf("Unexpected! -1 becomes large unsigned\\n");  // This prints
}

/* Pitfall 4: Truncation without warning */
int large = 300;
char small = large;  // Truncated to 44 (300 % 256)
printf("Truncated: %d\\n", small);

/* Pitfall 5: Floating-point to integer */
double d = 3.99999;
int i = d;  // Truncates (doesn't round!)
printf("Truncated: %d\\n", i);  // 3, not 4

/* Use explicit rounding */
#include <math.h>
int rounded = (int)round(d);  // Proper rounding
printf("Rounded: %d\\n", rounded);  // 4

/* Pitfall 6: Sign extension */
signed char sc = -1;  // 11111111 (all bits set)
int extended = sc;  // Sign-extended to 11111111 11111111 11111111 11111111
printf("Sign extended: %d\\n", extended);  // -1

unsigned char uc = 255;  // 11111111
int zero_extended = uc;  // Zero-extended to 00000000 00000000 00000000 11111111
printf("Zero extended: %d\\n", zero_extended);  // 255

/* Pitfall 7: Pointer cast alignment issues */
char buffer[100];
int *ip = (int*)buffer;  // May cause alignment issues on some platforms
// Safer:
int aligned_value;
memcpy(&aligned_value, buffer, sizeof(int));`} language="c" />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <p>
                    Following best practices for type conversion makes code safer, clearer, and more maintainable.
                    Explicit is better than implicit when clarity matters. Always consider potential data loss and use
                    appropriate types for your data.
                </p>

                <div className="code-example">
                    <CodeBlock code={`/* Best practice 1: Cast explicitly for clarity */
int sum = 100;
int count = 3;

// Good: Intention clear
double average = (double)sum / count;

// Bad: Unclear if intentional
double average2 = sum / count;  // Integer division?

/* Best practice 2: Check for overflow */
int a = 1000000;
int b = 1000000;

// Check before multiply
if (a &gt; INT_MAX / b) {
    printf("Would overflow!\\n");
} else {
    long long safe_product = (long long)a * b;
}

/* Best practice 3: Use appropriate types */
// Bad: Using float for money
float price = 19.99f;  // Precision issues!

// Good: Use integers for money (cents)
int price_cents = 1999;  // 19.99 represented as cents

/* Best practice 4: Avoid comparing signed and unsigned */
int signed_count = 10;
unsigned int unsigned_count = 5;

// Bad:
if (signed_count < unsigned_count) {"{ }"}

// Good: Cast explicitly
if (signed_count < (int)unsigned_count) {"{ }"}
// Or ensure both same type

/* Best practice 5: Use proper format specifiers */
long l = 123456789L;
printf("%ld\\n", l);  // Correct format

size_t s = sizeof(int);
printf("%zu\\n", s);  // Correct format for size_t

/* Best practice 6: Use stdint.h for fixed-size types */
#include <stdint.h>

int32_t fixed_32 = 42;  // Exactly 32 bits
uint16_t fixed_16 = 100;  // Exactly 16 bits unsigned

/* Best practice 7: Comment unusual casts */
// Cast to unsigned because bit pattern matters, not value
unsigned int bit_pattern = (unsigned int)signed_value;

/* Best practice 8: Use const with casts */
const int *cip = (const int*)&value;  // Adds const qualification`} language="c" />
                </div>
            </div>

            <div className="content-section">
                <h2>Summary & What's Next</h2>
                <KeyFacts><ul>
                        <li>Implicit conversion happens automatically by compiler</li>
                        <li>Explicit casting uses (type)value syntax</li>
                        <li>Widening conversions are safe, narrowing may lose data</li>
                        <li>Integer promotion converts small types to int</li>
                        <li>Cast before division to avoid integer truncation</li>
                        <li>Watch for overflow in intermediate calculations</li>
                        <li>Be careful mixing signed and unsigned types</li>
                        <li>Use explicit casts for clarity and safety</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default CTypeCasting;




